import { Panel, PageHeader } from "@/components/admin-shell";
import { apiRequest } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tag, Mail, Sparkles, Heart, Plus, Trash2, X, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface ICoupon {
  _id: string;
  code: string;
  type: "percent" | "flat";
  value: number;
  minSubtotal?: number;
  expiresAt?: string;
  active: boolean;
}

interface IQuestionOption {
  value: "fresh" | "warm" | "floral" | "woody";
  label: { bn: string; en: string };
}

interface IQuestion {
  _id: string;
  id: string;
  prompt: { bn: string; en: string };
  options: IQuestionOption[];
}

export default function MarketingPage() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  // Scent Finder Quiz State
  const [isQuizOpen, setIsQuizOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"list" | "add">("list");
  const [promptEn, setPromptEn] = useState("");
  const [promptBn, setPromptBn] = useState("");
  const [freshEn, setFreshEn] = useState("");
  const [freshBn, setFreshBn] = useState("");
  const [warmEn, setWarmEn] = useState("");
  const [warmBn, setWarmBn] = useState("");
  const [floralEn, setFloralEn] = useState("");
  const [floralBn, setFloralBn] = useState("");
  const [woodyEn, setWoodyEn] = useState("");
  const [woodyBn, setWoodyBn] = useState("");

  // Form State
  const [code, setCode] = useState("");
  const [type, setType] = useState<"percent" | "flat">("percent");
  const [value, setValue] = useState("");
  const [minSubtotal, setMinSubtotal] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  // Fetch Scent Finder Questions
  const { data: questions = [], isLoading: isQuestionsLoading } = useQuery<IQuestion[]>({
    queryKey: ["scent-questions"],
    queryFn: () => apiRequest<IQuestion[]>("/scent-finder/questions"),
  });

  // Create Quiz Question Mutation
  const createQuestionMutation = useMutation({
    mutationFn: (newQuestion: any) =>
      apiRequest<IQuestion>("/scent-finder/questions", "POST", newQuestion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scent-questions"] });
      toast.success("Quiz question created successfully");
      resetQuizForm();
      setActiveTab("list");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create quiz question");
    },
  });

  // Delete Quiz Question Mutation
  const deleteQuestionMutation = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/scent-finder/questions/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scent-questions"] });
      toast.success("Quiz question deleted successfully");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to delete quiz question");
    },
  });

  const resetQuizForm = () => {
    setPromptEn("");
    setPromptBn("");
    setFreshEn("");
    setFreshBn("");
    setWarmEn("");
    setWarmBn("");
    setFloralEn("");
    setFloralBn("");
    setWoodyEn("");
    setWoodyBn("");
  };

  const handleQuizSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !promptEn ||
      !promptBn ||
      !freshEn ||
      !freshBn ||
      !warmEn ||
      !warmBn ||
      !floralEn ||
      !floralBn ||
      !woodyEn ||
      !woodyBn
    ) {
      toast.error("Please fill in all options and question details");
      return;
    }

    const generatedId = promptEn
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

    if (!generatedId) {
      toast.error("Could not generate a valid ID slug from the English prompt");
      return;
    }

    const payload = {
      id: generatedId,
      prompt: { en: promptEn, bn: promptBn },
      options: [
        { value: "fresh", label: { en: freshEn, bn: freshBn } },
        { value: "warm", label: { en: warmEn, bn: warmBn } },
        { value: "floral", label: { en: floralEn, bn: floralBn } },
        { value: "woody", label: { en: woodyEn, bn: woodyBn } },
      ],
    };

    createQuestionMutation.mutate(payload);
  };

  // Fetch Coupons
  const {
    data: coupons = [],
    isLoading,
    error,
  } = useQuery<ICoupon[]>({
    queryKey: ["coupons"],
    queryFn: () => apiRequest<ICoupon[]>("/coupons"),
  });

  // Create Coupon Mutation
  const createMutation = useMutation({
    mutationFn: (newCoupon: any) => apiRequest<ICoupon>("/coupons", "POST", newCoupon),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
      toast.success("Coupon created successfully");
      setIsOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to create coupon");
    },
  });

  // Toggle Coupon Mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiRequest<ICoupon>(`/coupons/${id}`, "PATCH", { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
      toast.success("Coupon status updated");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to update coupon status");
    },
  });

  // Delete Coupon Mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/coupons/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
      toast.success("Coupon deleted successfully");
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to delete coupon");
    },
  });

  const resetForm = () => {
    setCode("");
    setType("percent");
    setValue("");
    setMinSubtotal("");
    setExpiresAt("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!code || !value) {
      toast.error("Please fill in coupon code and value");
      return;
    }

    const payload: any = {
      code: code.trim().toUpperCase(),
      type,
      value: Number(value),
      active: true,
    };

    if (minSubtotal) payload.minSubtotal = Number(minSubtotal);
    if (expiresAt) payload.expiresAt = new Date(expiresAt).toISOString();

    createMutation.mutate(payload);
  };

  // Scent option rows config — drives the compact table layout
  const scentOptions = [
    {
      key: "fresh",
      label: "Fresh",
      emoji: "🌊",
      en: freshEn,
      setEn: setFreshEn,
      bn: freshBn,
      setBn: setFreshBn,
    },
    {
      key: "warm",
      label: "Warm",
      emoji: "🔥",
      en: warmEn,
      setEn: setWarmEn,
      bn: warmBn,
      setBn: setWarmBn,
    },
    {
      key: "floral",
      label: "Floral",
      emoji: "🌸",
      en: floralEn,
      setEn: setFloralEn,
      bn: floralBn,
      setBn: setFloralBn,
    },
    {
      key: "woody",
      label: "Woody",
      emoji: "🌲",
      en: woodyEn,
      setEn: setWoodyEn,
      bn: woodyBn,
      setBn: setWoodyBn,
    },
  ] as const;

  return (
    <>
      <PageHeader
        title="Marketing & Conversion"
        subtitle="Coupons, FOMO, abandoned carts, scent quiz — every lever to grow revenue."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Coupons (spans 2) */}
        <Panel
          className="lg:col-span-2"
          title="Coupons & Discounts"
          description="Create percentage or flat-amount discount codes"
        >
          <div className="mb-4 flex justify-end">
            <button
              onClick={() => setIsOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-gold/30 px-3 py-1.5 text-xs text-gold hover:bg-accent cursor-pointer"
            >
              <Plus className="h-3.5 w-3.5" /> New coupon
            </button>
          </div>

          {isLoading ? (
            <div className="flex h-32 items-center justify-center text-gold">
              Loading coupons...
            </div>
          ) : error ? (
            <div className="text-red-400">Error loading coupons: {(error as Error).message}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-gold-soft">
                    <th className="py-2">Code</th>
                    <th>Type</th>
                    <th className="text-right">Value</th>
                    <th className="text-right">Min. Subtotal</th>
                    <th>Expires</th>
                    <th>Active</th>
                    <th className="text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gold/10">
                  {coupons.map((c) => (
                    <tr key={c._id}>
                      <td className="py-3 font-mono text-gold">{c.code}</td>
                      <td className="capitalize text-muted-foreground">{c.type}</td>
                      <td className="text-right">
                        {c.type === "percent" ? `${c.value}%` : `৳${c.value}`}
                      </td>
                      <td className="text-right text-muted-foreground">
                        {c.minSubtotal ? `৳${c.minSubtotal}` : "—"}
                      </td>
                      <td className="text-muted-foreground">
                        {c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : "Never"}
                      </td>
                      <td>
                        <button
                          onClick={() => toggleMutation.mutate({ id: c._id, active: !c.active })}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition cursor-pointer ${
                            c.active ? "bg-gold" : "bg-accent border border-gold/20"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 rounded-full bg-background transition ${
                              c.active ? "translate-x-4" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                      </td>
                      <td className="text-right">
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                `Are you sure you want to delete the coupon code "${c.code}"?`,
                              )
                            ) {
                              deleteMutation.mutate(c._id);
                            }
                          }}
                          className="rounded p-1 text-gold-soft hover:text-red-300 transition cursor-pointer"
                          title="Delete coupon"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="FOMO & Social proof">
          <div className="space-y-4 text-sm">
            <ToggleRow
              label="Live purchase popup"
              hint="Show recent buys from other customers"
              on
            />
            <ToggleRow label="Low stock urgency" hint="'Only X left in stock' message" on />
            <div>
              <label className="text-xs text-muted-foreground">Urgency threshold</label>
              <input
                type="number"
                defaultValue={5}
                className="mt-1 w-full rounded-md border border-gold/25 bg-background/40 px-3 py-2 text-foreground outline-none focus:border-gold"
              />
            </div>
            <ToggleRow label="Sale countdown timer" hint="Show timer for limited offers" />
          </div>
        </Panel>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Panel className="lg:col-span-3" title="Abandoned cart recovery">
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { label: "Carts in last 24h", value: 12, icon: Heart },
              { label: "Recovery emails sent", value: 24, icon: Mail },
              { label: "Recovered revenue", value: "৳18,400", icon: Sparkles },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-gold/15 bg-accent/30 p-4">
                <s.icon className="h-4 w-4 text-gold" />
                <div className="mt-2 font-display text-2xl">{s.value}</div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 ornate-divider" />
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <ToggleRow label="1-hour reminder email" hint="Send personalized email after 1h" on />
            <ToggleRow label="24-hour follow-up" hint="Second touch with discount link" on />
          </div>
        </Panel>
      </div>

      {/* Scent Finder Quiz Manager Section */}
      <div className="mt-6">
        <Panel
          title="Scent Finder Quiz questions"
          description="Bilingual quiz to engage visitors and guide them to their perfect fragrance profile."
        >
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-start gap-2.5">
              <Tag className="h-5 w-5 mt-0.5 text-gold" />
              <div className="text-sm text-muted-foreground">
                <span className="text-foreground font-semibold block">
                  {questions.length} questions configured
                </span>
                368 leads captured this month
              </div>
            </div>
            <button
              onClick={() => setIsQuizOpen(true)}
              className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 transition cursor-pointer"
            >
              <Plus className="h-3.5 w-3.5" /> Configure Quiz Builder
            </button>
          </div>

          <div className="ornate-divider my-4" />

          {/* List of Questions with roomy options */}
          {isQuestionsLoading ? (
            <div className="flex h-32 items-center justify-center text-sm text-gold">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading quiz questions...
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground italic">
              No quiz questions configured yet. Click "Configure Quiz Builder" to add your first
              question.
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {questions.map((q, idx) => (
                <div
                  key={q._id}
                  className="rounded-xl border border-gold/15 bg-accent/5 p-5 space-y-4 hover:border-gold/30 transition shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span className="font-mono text-[9px] bg-gold/15 text-gold px-2 py-0.5 rounded">
                        QUESTION #{idx + 1} (SLUG: {q.id})
                      </span>
                      <h4 className="text-sm font-semibold text-foreground mt-2 leading-snug">
                        EN: {q.prompt.en}
                      </h4>
                      <h4
                        className="text-sm font-semibold text-gold-soft mt-1 leading-snug"
                        lang="bn"
                      >
                        BN: {q.prompt.bn}
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete the quiz question "${q.prompt.en}"?`,
                          )
                        ) {
                          deleteQuestionMutation.mutate(q._id);
                        }
                      }}
                      disabled={deleteQuestionMutation.isPending}
                      className="rounded p-1.5 text-muted-foreground/60 hover:text-red-400 hover:bg-red-500/10 transition cursor-pointer shrink-0"
                      title="Delete question"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Roomy Scent Options */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-gold/10">
                    {q.options.map((opt) => {
                      const emoji = { fresh: "🌊", warm: "🔥", floral: "🌸", woody: "🌲" }[
                        opt.value
                      ];
                      return (
                        <div
                          key={opt.value}
                          className="rounded-lg bg-background/50 border border-gold/10 p-3 flex flex-col items-center text-center gap-1.5"
                        >
                          <span className="text-2xl leading-none">{emoji}</span>
                          <div className="w-full min-w-0">
                            <span className="text-[9px] font-bold text-gold uppercase tracking-wider block">
                              {opt.value}
                            </span>
                            <p className="text-xs font-semibold text-foreground truncate mt-1">
                              {opt.label.en}
                            </p>
                            <p className="text-[11px] text-muted-foreground truncate" lang="bn">
                              {opt.label.bn}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      {/* Add Coupon Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-md rounded-xl border border-gold/30 bg-brand-card p-6 shadow-2xl">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 rounded p-1 text-muted-foreground hover:text-gold"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="font-display text-2xl text-gold mb-2">New Discount Coupon</h2>
            <p className="text-xs text-muted-foreground mb-6">
              Create promotional or seasonal coupons.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                  Coupon Code *
                </label>
                <input
                  type="text"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="EID2026"
                  className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold uppercase"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Discount Type *
                  </label>
                  <select
                    value={type}
                    onChange={(e: any) => setType(e.target.value)}
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  >
                    <option value="percent">Percentage (%)</option>
                    <option value="flat">Flat Cash (BDT)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Value *
                  </label>
                  <input
                    type="number"
                    required
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="15"
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Min. Subtotal (BDT)
                  </label>
                  <input
                    type="number"
                    value={minSubtotal}
                    onChange={(e) => setMinSubtotal(e.target.value)}
                    placeholder="2000"
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-gold-soft mb-1">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full rounded border border-gold/25 bg-accent/25 px-3 py-2 text-sm text-foreground outline-none focus:border-gold"
                  />
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded border border-gold/30 px-4 py-2 text-sm text-gold hover:bg-accent cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="rounded bg-gold px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50 cursor-pointer"
                >
                  {createMutation.isPending ? "Creating..." : "Save Coupon"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── SCENT FINDER QUIZ BUILDER MODAL ── */}
      {isQuizOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="relative w-full max-w-2xl rounded-xl border border-gold/30 bg-brand-card shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
            {/* Modal header */}
            <div className="flex items-start justify-between px-6 py-5 border-b border-gold/15 shrink-0">
              <div>
                <h2 className="font-display text-xl text-gold leading-snug">
                  Scent Finder Quiz Builder
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Manage questions and answers to guide users to their signature scent.
                </p>
              </div>
              <button
                onClick={() => setIsQuizOpen(false)}
                className="ml-4 rounded p-1.5 text-muted-foreground hover:text-gold hover:bg-accent/40 transition cursor-pointer shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Tabs Selector */}
            <div className="flex border-b border-gold/15 px-6 shrink-0 bg-accent/5">
              <button
                type="button"
                onClick={() => setActiveTab("list")}
                className={`px-4 py-3 text-xs uppercase tracking-wider font-semibold border-b-2 transition-all cursor-pointer ${
                  activeTab === "list"
                    ? "border-gold text-gold"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                Active Questions ({questions.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("add")}
                className={`px-4 py-3 text-xs uppercase tracking-wider font-semibold border-b-2 transition-all cursor-pointer ${
                  activeTab === "add"
                    ? "border-gold text-gold"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                Add New Question
              </button>
            </div>

            {/* Modal body */}
            <div className="flex-1 overflow-hidden flex flex-col">
              {activeTab === "list" ? (
                /* ── TAB 1: Questions List ── */
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {isQuestionsLoading ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-gold" />
                    </div>
                  ) : questions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
                      <span className="text-4xl">✨</span>
                      <p className="text-sm text-muted-foreground">
                        No questions yet. Click "Add New Question" to create your first one.
                      </p>
                    </div>
                  ) : (
                    questions.map((q) => (
                      <div
                        key={q._id}
                        className="group rounded-lg border border-gold/15 bg-accent/10 p-5 space-y-3.5 hover:border-gold/30 transition relative"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <span className="font-mono text-[9px] bg-gold/15 text-gold px-1.5 py-0.5 rounded">
                              SLUG: {q.id}
                            </span>
                            <h4 className="text-sm font-semibold text-foreground mt-2 leading-snug">
                              EN: {q.prompt.en}
                            </h4>
                            <h4
                              className="text-sm font-semibold text-gold-soft mt-1 leading-snug"
                              lang="bn"
                            >
                              BN: {q.prompt.bn}
                            </h4>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Are you sure you want to delete the quiz question "${q.prompt.en}"?`,
                                )
                              ) {
                                deleteQuestionMutation.mutate(q._id);
                              }
                            }}
                            disabled={deleteQuestionMutation.isPending}
                            className="rounded p-1.5 text-muted-foreground/60 hover:text-red-400 hover:bg-red-500/10 transition cursor-pointer shrink-0"
                            title="Delete question"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Options */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-3 border-t border-gold/10">
                          {q.options.map((opt) => {
                            const emoji = { fresh: "🌊", warm: "🔥", floral: "🌸", woody: "🌲" }[
                              opt.value
                            ];
                            return (
                              <div
                                key={opt.value}
                                className="rounded-md bg-background/50 border border-gold/10 p-2.5 flex items-start gap-2"
                              >
                                <span className="text-lg leading-none">{emoji}</span>
                                <div className="min-w-0">
                                  <span className="text-[9px] font-semibold text-gold uppercase tracking-wider block">
                                    {opt.value}
                                  </span>
                                  <p className="text-xs font-medium text-foreground truncate mt-0.5">
                                    {opt.label.en}
                                  </p>
                                  <p
                                    className="text-[11px] text-muted-foreground truncate"
                                    lang="bn"
                                  >
                                    {opt.label.bn}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                /* ── TAB 2: Add Question Form ── */
                <form
                  onSubmit={handleQuizSubmit}
                  className="flex-1 overflow-y-auto p-6 space-y-6 flex flex-col justify-between"
                >
                  <div className="space-y-6">
                    {/* Step 1 — Question Prompts */}
                    <div className="space-y-3.5">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gold/20 text-gold text-[10px] font-bold shrink-0">
                          1
                        </span>
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                          Question Prompts
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                            English Prompt <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={promptEn}
                            onChange={(e) => setPromptEn(e.target.value)}
                            placeholder="e.g. Which environment do you love most?"
                            className="w-full rounded-md border border-gold/25 bg-accent/20 px-3 py-2 text-xs text-foreground outline-none focus:border-gold transition"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                            বাংলা প্রম্পট <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            required
                            value={promptBn}
                            onChange={(e) => setPromptBn(e.target.value)}
                            placeholder="যেমন: কোন পরিবেশ আপনার সবচেয়ে পছন্দ?"
                            className="w-full rounded-md border border-gold/25 bg-accent/20 px-3 py-2 text-xs text-foreground outline-none focus:border-gold transition"
                            lang="bn"
                          />
                        </div>
                      </div>

                      {/* Auto-Generated Slug Preview */}
                      <div className="bg-accent/10 border border-gold/10 rounded-md p-3 flex items-center justify-between gap-4">
                        <div className="text-[11px] text-muted-foreground">
                          <span className="font-semibold text-gold mr-1">Generated ID (Slug):</span>
                          {promptEn ? (
                            <code className="font-mono text-foreground bg-gold/10 px-1.5 py-0.5 rounded">
                              {promptEn
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/(^-|-$)/g, "")}
                            </code>
                          ) : (
                            <span className="italic text-muted-foreground/60">
                              Type an English prompt to preview slug
                            </span>
                          )}
                        </div>
                        <span className="text-[9px] font-mono text-muted-foreground uppercase">
                          Saved as primary key
                        </span>
                      </div>
                    </div>

                    {/* Step 2 — Scent Option Matches */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gold/20 text-gold text-[10px] font-bold shrink-0">
                          2
                        </span>
                        <span className="text-xs font-semibold text-foreground uppercase tracking-wider">
                          Scent Option Matches
                        </span>
                      </div>

                      {/* Header titles */}
                      <div className="grid grid-cols-[90px_1fr_1fr] gap-2 px-2">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          Scent Profile
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          English Label *
                        </span>
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          বাংলা Label *
                        </span>
                      </div>

                      {/* Table list */}
                      <div className="rounded-lg border border-gold/20 overflow-hidden divide-y divide-gold/10">
                        {scentOptions.map(({ key, label, emoji, en, setEn, bn, setBn }) => (
                          <div
                            key={key}
                            className="grid grid-cols-[90px_1fr_1fr] gap-2.5 items-center px-3 py-2.5 bg-accent/5 hover:bg-accent/15 transition focus-within:bg-accent/15"
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-base leading-none">{emoji}</span>
                              <span className="text-xs font-medium text-foreground">{label}</span>
                            </div>

                            <input
                              type="text"
                              required
                              value={en}
                              onChange={(e) => setEn(e.target.value)}
                              placeholder={`e.g. ${["Ocean breeze", "Warm vanilla", "Fresh roses", "Sandalwood"][["fresh", "warm", "floral", "woody"].indexOf(key)]}`}
                              className="w-full rounded border border-gold/20 bg-background/60 px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-gold transition"
                            />

                            <input
                              type="text"
                              required
                              value={bn}
                              onChange={(e) => setBn(e.target.value)}
                              placeholder={`বাংলা অনুবাদ`}
                              className="w-full rounded border border-gold/20 bg-background/60 px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-gold transition"
                              lang="bn"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gold/15 mt-4">
                    <button
                      type="button"
                      onClick={resetQuizForm}
                      className="text-xs text-muted-foreground hover:text-gold transition cursor-pointer"
                    >
                      Clear form
                    </button>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setIsQuizOpen(false)}
                        className="rounded-md border border-gold/30 px-4 py-2 text-xs text-gold hover:bg-accent cursor-pointer transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={createQuestionMutation.isPending}
                        className="rounded-md bg-gold px-5 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5 transition"
                      >
                        {createQuestionMutation.isPending ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Adding…
                          </>
                        ) : (
                          "Add Question"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ToggleRow({ label, hint, on }: { label: string; hint: string; on?: boolean }) {
  const [toggle, setToggle] = useState(!!on);
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-gold/15 bg-accent/30 p-3">
      <div>
        <div className="text-sm text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
      <button
        onClick={() => setToggle(!toggle)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition cursor-pointer ${
          toggle ? "bg-gold" : "bg-accent border border-gold/20"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-background transition ${
            toggle ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </button>
    </div>
  );
}
